import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AlertTriangle, Boxes, Edit, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { BatchType, BatchTypePackage, PackagingType } from '../../../../types/batchPickingSetup';
import { batchPickingSetupService } from '../../../../services/batchPickingSetupService';
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils';
import type { ColumnDefinition, ExpandableRowConfig, TableAction } from '../../../shared/table/types/table';
import { NumericInput } from '../../../common/inputs';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BatchTypePackagesModal, compactDimensions, isNotAscending } from './BatchTypePackagesModal';

interface BatchPickingSetupAppProps {
  onClose?: () => void;
  onBack?: () => void;
}

type Tab = 'types' | 'packaging';

interface BatchTypeFormData {
  pListBatchTypeNo: string;
  description: string;
  numOfPackages: string;
}

interface PackagingTypeFormData {
  packagingTypeNo: string;
  description: string;
  height: string;
  width: string;
  length: string;
  volume: string;
  fillFactorPercent: string;
  maxWeight: string;
  maxLength: string;
  maxNumberOfLines: string;
}

const emptyPackagingForm: PackagingTypeFormData = {
  packagingTypeNo: '', description: '', height: '0', width: '0', length: '0', volume: '0',
  fillFactorPercent: '100', maxWeight: '', maxLength: '0', maxNumberOfLines: '0'
};

const toNumber = (value: string) => (value.trim() === '' ? 0 : parseFloat(value));
const nextNo = (numbers: number[]) => String((numbers.length ? Math.max(...numbers) : 0) + 1);

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

/**
 * System → Samplukk (TW 36450120). Vedlikehold av samplukk-typer (PListBatchType), esker
 * (PackagingType) og hvilke esker en type kan bruke (PListBatchTypePackages). Bygget etter
 * LoadingZoneApp-mønsteret (WarehouseTable + modal), med faner som ExternalSystemApp.
 */
export const BatchPickingSetupApp: React.FC<BatchPickingSetupAppProps> = ({ onClose, onBack }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('packaging');

  const [batchTypes, setBatchTypes] = useState<BatchType[]>([]);
  const [packagingTypes, setPackagingTypes] = useState<PackagingType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTypeIds, setSelectedTypeIds] = useState<number[]>([]);
  const [selectedPackagingIds, setSelectedPackagingIds] = useState<number[]>([]);

  const [typeForm, setTypeForm] = useState<BatchTypeFormData | null>(null);
  const [editingTypeNo, setEditingTypeNo] = useState<number | null>(null);
  const [packagingForm, setPackagingForm] = useState<PackagingTypeFormData | null>(null);
  const [editingPackagingNo, setEditingPackagingNo] = useState<number | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [packagesFor, setPackagesFor] = useState<BatchType | null>(null);
  // Esker per samplukk-type for ekspanderte linjer — hentes når linjen åpnes
  const [packagesByType, setPackagesByType] = useState<Record<number, BatchTypePackage[] | null>>({});

  const loadPackagesForType = useCallback(async (batchTypeNo: number) => {
    setPackagesByType((prev) => ({ ...prev, [batchTypeNo]: null }));
    try {
      const packages = await batchPickingSetupService.getBatchTypePackages(batchTypeNo);
      setPackagesByType((prev) => ({ ...prev, [batchTypeNo]: packages }));
    } catch (err) {
      console.error('Error loading batch type packages:', err);
      setPackagesByType((prev) => ({ ...prev, [batchTypeNo]: [] }));
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPackagesByType({});
    try {
      const [types, packaging] = await Promise.all([
        batchPickingSetupService.getBatchTypes(),
        batchPickingSetupService.getPackagingTypes()
      ]);
      setBatchTypes(types);
      setPackagingTypes(packaging);
    } catch (err) {
      console.error('Error loading batch picking setup:', err);
      setError(t('manager.modules.batchPickingSetup.errors.loadFailed', {}, 'Feil ved lasting av samplukk-oppsettet'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ==================== SAMPLUKK-TYPER ====================

  const selectedType = selectedTypeIds.length === 1
    ? batchTypes.find((bt) => bt.pListBatchTypeNo === selectedTypeIds[0]) ?? null
    : null;

  const openCreateType = useCallback(() => {
    setEditingTypeNo(null);
    setTypeForm({ pListBatchTypeNo: nextNo(batchTypes.map((bt) => bt.pListBatchTypeNo)), description: '', numOfPackages: '0' });
    setFormError(null);
  }, [batchTypes]);

  const openEditType = useCallback((row: BatchType | null) => {
    if (!row) return;
    setEditingTypeNo(row.pListBatchTypeNo);
    setTypeForm({ pListBatchTypeNo: String(row.pListBatchTypeNo), description: row.description ?? '', numOfPackages: String(row.numOfPackages) });
    setFormError(null);
  }, []);

  const handleDeleteTypes = useCallback(async () => {
    if (selectedTypeIds.length === 0) return;
    const confirmed = window.confirm(
      t('manager.modules.batchPickingSetup.types.confirmDelete', { '0': selectedTypeIds.length },
        `Slette ${selectedTypeIds.length} samplukk-type(r)? Eskekoblingene til typen slettes også.`)
    );
    if (!confirmed) return;
    setError(null);
    try {
      for (const id of selectedTypeIds) {
        await batchPickingSetupService.deleteBatchType(id);
      }
      setSelectedTypeIds([]);
    } catch (err) {
      console.error('Error deleting batch types:', err);
      setError(err instanceof Error ? err.message : t('manager.modules.batchPickingSetup.errors.deleteFailed', {}, 'Feil ved sletting'));
    } finally {
      await loadData();
    }
  }, [selectedTypeIds, loadData, t]);

  const handleSubmitType = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!typeForm) return;

    const no = parseInt(typeForm.pListBatchTypeNo, 10);
    if (isNaN(no) || no <= 0) {
      setFormError(t('manager.modules.batchPickingSetup.errors.noRequired', {}, 'Nr (tall større enn 0) er påkrevd'));
      return;
    }
    if (!typeForm.description.trim()) {
      setFormError(t('manager.modules.batchPickingSetup.errors.descriptionRequired', {}, 'Beskrivelse er påkrevd'));
      return;
    }

    const request = { pListBatchTypeNo: no, description: typeForm.description.trim(), numOfPackages: Math.trunc(toNumber(typeForm.numOfPackages)) };
    setFormLoading(true);
    setFormError(null);
    try {
      if (editingTypeNo == null) {
        await batchPickingSetupService.createBatchType(request);
      } else {
        await batchPickingSetupService.updateBatchType(editingTypeNo, request);
      }
      setTypeForm(null);
      setSelectedTypeIds([]);
      await loadData();
    } catch (err) {
      console.error('Error saving batch type:', err);
      setFormError(err instanceof Error ? err.message : t('manager.modules.batchPickingSetup.errors.saveFailed', {}, 'Feil ved lagring'));
    } finally {
      setFormLoading(false);
    }
  };

  // ==================== ESKER ====================

  const openCreatePackaging = useCallback(() => {
    setEditingPackagingNo(null);
    setPackagingForm({ ...emptyPackagingForm, packagingTypeNo: nextNo(packagingTypes.map((pt) => pt.packagingTypeNo)) });
    setFormError(null);
  }, [packagingTypes]);

  const openEditPackaging = useCallback((row: PackagingType | undefined) => {
    if (!row) return;
    setEditingPackagingNo(row.packagingTypeNo);
    setPackagingForm({
      packagingTypeNo: String(row.packagingTypeNo),
      description: row.description,
      height: String(row.height),
      width: String(row.width),
      length: String(row.length),
      volume: String(row.volume),
      fillFactorPercent: String(Number((row.fillFactor * 100).toFixed(2))),
      maxWeight: String(row.maxWeight),
      maxLength: String(row.maxLength),
      maxNumberOfLines: String(row.maxNumberOfLines)
    });
    setFormError(null);
  }, []);

  const handleDeletePackaging = useCallback(async () => {
    if (selectedPackagingIds.length === 0) return;
    const confirmed = window.confirm(
      t('manager.modules.batchPickingSetup.packaging.confirmDelete', { '0': selectedPackagingIds.length }, `Slette ${selectedPackagingIds.length} eske(r)?`)
    );
    if (!confirmed) return;
    setError(null);
    try {
      for (const id of selectedPackagingIds) {
        await batchPickingSetupService.deletePackagingType(id);
      }
      setSelectedPackagingIds([]);
    } catch (err) {
      console.error('Error deleting packaging types:', err);
      setError(err instanceof Error ? err.message : t('manager.modules.batchPickingSetup.errors.deleteFailed', {}, 'Feil ved sletting'));
    } finally {
      await loadData();
    }
  }, [selectedPackagingIds, loadData, t]);

  const handleSubmitPackaging = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!packagingForm) return;

    const no = parseInt(packagingForm.packagingTypeNo, 10);
    if (isNaN(no) || no <= 0) {
      setFormError(t('manager.modules.batchPickingSetup.errors.noRequired', {}, 'Nr (tall større enn 0) er påkrevd'));
      return;
    }
    if (!packagingForm.description.trim()) {
      setFormError(t('manager.modules.batchPickingSetup.errors.descriptionRequired', {}, 'Beskrivelse er påkrevd'));
      return;
    }
    const fillFactorPercent = toNumber(packagingForm.fillFactorPercent);
    if (fillFactorPercent <= 0 || fillFactorPercent > 100) {
      setFormError(t('manager.modules.batchPickingSetup.errors.fillFactor', {}, 'Fyllingsgrad må være større enn 0 % og høyst 100 %'));
      return;
    }
    const maxWeight = toNumber(packagingForm.maxWeight);
    if (maxWeight <= 0) {
      setFormError(t('manager.modules.batchPickingSetup.errors.maxWeight', {}, 'Maks vekt må være større enn 0'));
      return;
    }

    const request = {
      packagingTypeNo: no,
      description: packagingForm.description.trim(),
      height: toNumber(packagingForm.height),
      width: toNumber(packagingForm.width),
      length: toNumber(packagingForm.length),
      volume: toNumber(packagingForm.volume),
      fillFactor: fillFactorPercent / 100,
      maxWeight,
      maxLength: toNumber(packagingForm.maxLength),
      maxNumberOfLines: Math.trunc(toNumber(packagingForm.maxNumberOfLines))
    };
    setFormLoading(true);
    setFormError(null);
    try {
      if (editingPackagingNo == null) {
        await batchPickingSetupService.createPackagingType(request);
      } else {
        await batchPickingSetupService.updatePackagingType(editingPackagingNo, request);
      }
      setPackagingForm(null);
      setSelectedPackagingIds([]);
      await loadData();
    } catch (err) {
      console.error('Error saving packaging type:', err);
      setFormError(err instanceof Error ? err.message : t('manager.modules.batchPickingSetup.errors.saveFailed', {}, 'Feil ved lagring'));
    } finally {
      setFormLoading(false);
    }
  };

  // ==================== TABELLER ====================

  const typeColumnFactory = createColumnFactory<BatchType>();
  const packagingColumnFactory = createColumnFactory<PackagingType>();

  const updatedByLabel = t('manager.modules.adminDelivery.common.columns.updatedBy', {}, 'Oppdatert av');
  const lastUpdateLabel = t('manager.modules.adminDelivery.common.columns.lastUpdate', {}, 'Sist oppdatert');

  const typeColumns: ColumnDefinition<BatchType>[] = useMemo(() => [
    typeColumnFactory.createNumberColumn('pListBatchTypeNo', 'pListBatchTypeNo', t('manager.modules.batchPickingSetup.fields.no', {}, 'Nr'), { width: 90, sortable: true, filterType: 'text' }),
    typeColumnFactory.createTextColumn('description', 'description', t('common.fields.description', {}, 'Beskrivelse'), { width: 300, sortable: true, filterType: 'text' }),
    typeColumnFactory.createNumberColumn('numOfPackages', 'numOfPackages', t('manager.modules.batchPickingSetup.fields.numOfCustomers', {}, 'Antall kunder'), { width: 130, sortable: true, filterType: 'text' }),
    typeColumnFactory.createTextColumn('updatedBy', 'updatedBy', updatedByLabel, { width: 120, sortable: true, filterType: 'text' }),
    typeColumnFactory.createDateColumn('lastUpdate', 'lastUpdate', lastUpdateLabel, { width: 130, sortable: true, filterType: 'text' })
  ], [t, updatedByLabel, lastUpdateLabel]);

  const packagingColumns: ColumnDefinition<PackagingType>[] = useMemo(() => [
    packagingColumnFactory.createNumberColumn('packagingTypeNo', 'packagingTypeNo', t('manager.modules.batchPickingSetup.fields.no', {}, 'Nr'), { width: 90, sortable: true, filterType: 'text' }),
    packagingColumnFactory.createTextColumn('description', 'description', t('common.fields.description', {}, 'Beskrivelse'), { width: 240, sortable: true, filterType: 'text' }),
    packagingColumnFactory.createNumberColumn('height', 'height', t('manager.modules.batchPickingSetup.fields.height', {}, 'Høyde'), { width: 90, sortable: true }),
    packagingColumnFactory.createNumberColumn('width', 'width', t('manager.modules.batchPickingSetup.fields.width', {}, 'Bredde'), { width: 90, sortable: true }),
    packagingColumnFactory.createNumberColumn('length', 'length', t('manager.modules.batchPickingSetup.fields.length', {}, 'Lengde'), { width: 90, sortable: true }),
    packagingColumnFactory.createNumberColumn('volume', 'volume', t('manager.modules.batchPickingSetup.fields.volume', {}, 'Volum'), { width: 90, sortable: true }),
    packagingColumnFactory.createNumberColumn('fillFactor', 'fillFactor', t('manager.modules.batchPickingSetup.fields.fillFactor', {}, 'Fyllingsgrad (%)'), {
      width: 130, sortable: true, formatter: (value: number) => `${Number((value * 100).toFixed(2))} %`
    }),
    packagingColumnFactory.createNumberColumn('maxWeight', 'maxWeight', t('manager.modules.batchPickingSetup.fields.maxWeight', {}, 'Maks vekt'), { width: 110, sortable: true }),
    packagingColumnFactory.createNumberColumn('maxLength', 'maxLength', t('manager.modules.batchPickingSetup.fields.maxLength', {}, 'Maks lengde'), { width: 110, sortable: true }),
    packagingColumnFactory.createNumberColumn('maxNumberOfLines', 'maxNumberOfLines', t('manager.modules.batchPickingSetup.fields.maxNumberOfLines', {}, 'Maks ordrelinjer'), { width: 130, sortable: true }),
    packagingColumnFactory.createTextColumn('updatedBy', 'updatedBy', updatedByLabel, { width: 120, sortable: true, filterType: 'text' }),
    packagingColumnFactory.createDateColumn('lastUpdate', 'lastUpdate', lastUpdateLabel, { width: 130, sortable: true, filterType: 'text' })
  ], [t, updatedByLabel, lastUpdateLabel]);

  const typeActions: TableAction<BatchType>[] = useMemo(() => [
    { id: 'create-batchtype', label: t('manager.modules.batchPickingSetup.actions.createNew', {}, 'Opprett ny'), icon: Plus, onClick: openCreateType, variant: 'primary' as const },
    { id: 'edit-batchtype', label: t('common.buttons.edit', {}, 'Rediger'), icon: Edit, onClick: () => openEditType(selectedType), disabled: () => selectedTypeIds.length !== 1, variant: 'primary' as const },
    { id: 'packages-batchtype', label: t('manager.modules.batchPickingSetup.actions.packages', {}, 'Esker i samplukket'), icon: Boxes, onClick: () => setPackagesFor(selectedType), disabled: () => selectedTypeIds.length !== 1, variant: 'primary' as const },
    { id: 'delete-batchtype', label: t('manager.modules.adminDelivery.common.delete', { '0': selectedTypeIds.length }, `Slett (${selectedTypeIds.length})`), icon: Trash2, onClick: handleDeleteTypes, disabled: () => selectedTypeIds.length === 0, variant: 'secondary' as const },
    { id: 'refresh-batchtypes', label: t('common.buttons.refresh', {}, 'Oppdater'), icon: RefreshCw, onClick: () => loadData(), variant: 'secondary' as const }
  ], [selectedTypeIds.length, selectedType, openCreateType, openEditType, handleDeleteTypes, loadData, t]);

  const packagingActions: TableAction<PackagingType>[] = useMemo(() => [
    { id: 'create-packaging', label: t('manager.modules.batchPickingSetup.actions.createNew', {}, 'Opprett ny'), icon: Plus, onClick: openCreatePackaging, variant: 'primary' as const },
    {
      id: 'edit-packaging', label: t('common.buttons.edit', {}, 'Rediger'), icon: Edit,
      onClick: () => openEditPackaging(packagingTypes.find((pt) => pt.packagingTypeNo === selectedPackagingIds[0])),
      disabled: () => selectedPackagingIds.length !== 1, variant: 'primary' as const
    },
    { id: 'delete-packaging', label: t('manager.modules.adminDelivery.common.delete', { '0': selectedPackagingIds.length }, `Slett (${selectedPackagingIds.length})`), icon: Trash2, onClick: handleDeletePackaging, disabled: () => selectedPackagingIds.length === 0, variant: 'secondary' as const },
    { id: 'refresh-packaging', label: t('common.buttons.refresh', {}, 'Oppdater'), icon: RefreshCw, onClick: () => loadData(), variant: 'secondary' as const }
  ], [selectedPackagingIds, packagingTypes, openCreatePackaging, openEditPackaging, handleDeletePackaging, loadData, t]);

  const tableConfig = (persistenceKey: string) => ({
    persistenceKey,
    showPagination: true,
    showColumnConfig: true,
    showGlobalSearch: true,
    showFilters: true,
    showExport: true,
    showPrint: true,
    showSummary: false,
    defaultPageSize: 100,
    compactMode: true,
    stickyHeader: true,
    allowColumnReorder: true,
    allowColumnResize: true,
    allowRowSelection: { mode: 'multi' as const, selectAllMode: 'current-page' as const }
  });

  // Pil per samplukk-type: viser eskene som inngår, i rekkefølgen motoren prøver dem
  const typeExpandableConfig: ExpandableRowConfig<BatchType, BatchTypePackage[] | null | undefined> = useMemo(() => ({
    getDetailData: (row: BatchType) => packagesByType[row.pListBatchTypeNo],
    onExpand: (row: BatchType) => {
      if (packagesByType[row.pListBatchTypeNo] === undefined) loadPackagesForType(row.pListBatchTypeNo);
    },
    expandOnRowClick: false,
    renderDetail: (row: BatchType, packages: BatchTypePackage[] | null | undefined) => {
      if (!packages) {
        return <p className="px-4 py-2 text-sm text-gray-500">{t('common.status.loading', {}, 'Laster...')}</p>;
      }
      if (packages.length === 0) {
        return (
          <p className="px-4 py-2 text-sm text-gray-500">
            {t('manager.modules.batchPickingSetup.packages.empty', {}, 'Ingen esker er lagt til. Uten esker blir ingen plukklister samplukket med denne typen.')}
          </p>
        );
      }
      const volLabel = t('manager.modules.batchPickingSetup.packages.volShort', {}, 'vol');
      const maxLabel = t('manager.modules.batchPickingSetup.packages.maxShort', {}, 'maks');
      return (
        <div className="px-4 py-2 space-y-1">
          {isNotAscending(packages) && (
            <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
              <p className="text-yellow-800 text-sm">
                {t('manager.modules.batchPickingSetup.packages.notAscending', {}, 'Rekkefølgen går ikke fra minst til størst volum. En større eske over en mindre gjør at den mindre sjelden blir valgt.')}
              </p>
            </div>
          )}
          <table className="text-sm">
            <thead>
              <tr className="text-left text-gray-600">
                <th className="pr-6 font-medium">{t('manager.modules.batchPickingSetup.fields.batchType', {}, 'Samplukk-type')}</th>
                <th className="pr-6 font-medium">{t('manager.modules.batchPickingSetup.fields.packagingType', {}, 'Eske')}</th>
                <th className="pr-6 font-medium text-right">{t('manager.modules.batchPickingSetup.packages.quantity', {}, 'Antall')}</th>
                <th className="pr-6 font-medium text-right">{t('manager.modules.batchPickingSetup.fields.sortId', {}, 'Sortering')}</th>
                <th className="font-medium">{t('manager.modules.batchPickingSetup.fields.dimensions', {}, 'Mål')}</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((p) => (
                <tr key={p.packagingTypeNo} className="text-gray-900">
                  <td className="pr-6">{p.pListBatchTypeNo}</td>
                  <td className="pr-6">{p.packagingTypeNo} – {p.description}</td>
                  <td className="pr-6 text-right">{p.numOfPackages ?? '–'}</td>
                  <td className="pr-6 text-right">{p.sortID}</td>
                  <td className="text-gray-600">{compactDimensions(p, volLabel, maxLabel)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
  }), [packagesByType, loadPackagesForType, t]);

  const packagingField = (field: keyof PackagingTypeFormData, label: string, mode: 'integer' | 'decimal' = 'decimal', disabled = false) => (
    <div>
      <label className={labelClass}>{label}</label>
      <NumericInput
        mode={mode}
        value={packagingForm?.[field] ?? ''}
        onChange={(e) => { setPackagingForm((prev) => (prev ? { ...prev, [field]: e.target.value } : prev)); setFormError(null); }}
        disabled={formLoading || disabled}
        className={inputClass}
      />
    </div>
  );

  const modalHeader = (title: string, onCloseModal: () => void) => (
    <div className="flex items-center justify-between p-6 border-b">
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      <button onClick={onCloseModal} className="text-gray-400 hover:text-gray-600 text-xl" aria-label={t('manager.modules.adminDelivery.common.closeModal', {}, 'Lukk modal')}>×</button>
    </div>
  );

  const modalButtons = (onCancel: () => void) => (
    <div className="flex justify-end gap-3 pt-4 border-t">
      <button type="button" onClick={onCancel} disabled={formLoading} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50">
        {t('common.buttons.cancel', {}, 'Avbryt')}
      </button>
      <button type="submit" disabled={formLoading} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
        {formLoading ? t('manager.modules.adminDelivery.common.saving', {}, 'Lagrer...') : t('common.buttons.save', {}, 'Lagre')}
      </button>
    </div>
  );

  const formErrorBox = formError && (
    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
      <p className="text-red-800 text-sm">{formError}</p>
    </div>
  );

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-white border-b">
        <div className="flex items-center gap-3">
          <BackButton onClick={onBack || (() => {})} />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{t('manager.modules.batchPickingSetup.title', {}, 'Samplukk')}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {t('manager.modules.batchPickingSetup.subtitle', {}, 'Samplukk-typer og esker som regelmotoren bruker når plukklister settes sammen til samplukk')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onClose && (
            <button onClick={onClose} className="px-3 py-2 text-sm bg-gray-500 text-white rounded hover:bg-gray-600">
              {t('common.buttons.close', {}, 'Lukk')}
            </button>
          )}
        </div>
      </div>

      {/* Faner */}
      <div className="flex gap-1 px-4 bg-white border-b">
        {([
          ['packaging', t('manager.modules.batchPickingSetup.tabs.packaging', {}, 'Samplukk-konfigurasjon (esker)')],
          ['types', t('manager.modules.batchPickingSetup.tabs.types', {}, 'Samplukk-typer')]
        ] as const).map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`px-4 py-2 text-sm font-medium ${activeTab === key
              ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50'
              : 'text-gray-600 hover:text-gray-800'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0">
        {activeTab === 'types' ? (
          <WarehouseTable
            key="batchpicking-types"
            data={batchTypes}
            columns={typeColumns}
            loading={loading}
            error={error || undefined}
            config={tableConfig('batchpicking-types-table')}
            selectedRows={selectedTypeIds}
            onSelectionChange={(ids: (string | number)[]) => setSelectedTypeIds(ids.map(Number))}
            getRowId={(row: BatchType) => row.id}
            tableActions={typeActions}
            actions={{ onRowDoubleClick: (row: BatchType) => setPackagesFor(row) }}
            expandableConfig={typeExpandableConfig}
            aria-label={t('manager.modules.batchPickingSetup.types.tableAriaLabel', {}, 'Samplukk-typer tabell')}
          />
        ) : (
          <WarehouseTable
            key="batchpicking-packaging"
            data={packagingTypes}
            columns={packagingColumns}
            loading={loading}
            error={error || undefined}
            config={tableConfig('batchpicking-packaging-table')}
            selectedRows={selectedPackagingIds}
            onSelectionChange={(ids: (string | number)[]) => setSelectedPackagingIds(ids.map(Number))}
            getRowId={(row: PackagingType) => row.id}
            tableActions={packagingActions}
            actions={{ onRowDoubleClick: (row: PackagingType) => openEditPackaging(row) }}
            aria-label={t('manager.modules.batchPickingSetup.packaging.tableAriaLabel', {}, 'Esker tabell')}
          />
        )}
      </div>

      {/* Samplukk-type: opprett/rediger */}
      {typeForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            {modalHeader(editingTypeNo == null
              ? t('manager.modules.batchPickingSetup.types.createTitle', {}, 'Opprett samplukk-type')
              : t('manager.modules.batchPickingSetup.types.editTitleNo', { '0': editingTypeNo }, `Rediger samplukk-type ${editingTypeNo}`), () => setTypeForm(null))}
            <form onSubmit={handleSubmitType} className="p-6 space-y-4">
              {formErrorBox}
              {/* Nr settes bare ved opprettelse — ved redigering endres kun Beskrivelse og Antall kunder */}
              {editingTypeNo == null && (
                <div>
                  <label className={labelClass}>{t('manager.modules.batchPickingSetup.fields.noRequired', {}, 'Nr *')}</label>
                  <NumericInput
                    mode="integer"
                    value={typeForm.pListBatchTypeNo}
                    onChange={(e) => { setTypeForm({ ...typeForm, pListBatchTypeNo: e.target.value }); setFormError(null); }}
                    disabled={formLoading}
                    className={inputClass}
                  />
                </div>
              )}
              <div>
                <label className={labelClass}>{t('common.fields.description', {}, 'Beskrivelse')} *</label>
                <input
                  type="text"
                  value={typeForm.description}
                  onChange={(e) => { setTypeForm({ ...typeForm, description: e.target.value }); setFormError(null); }}
                  disabled={formLoading}
                  className={inputClass}
                  maxLength={100}
                />
              </div>
              <div>
                <label className={labelClass}>{t('manager.modules.batchPickingSetup.fields.numOfCustomers', {}, 'Antall kunder')}</label>
                <NumericInput
                  mode="integer"
                  value={typeForm.numOfPackages}
                  onChange={(e) => { setTypeForm({ ...typeForm, numOfPackages: e.target.value }); setFormError(null); }}
                  disabled={formLoading}
                  className={inputClass}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('manager.modules.batchPickingSetup.types.numOfCustomersHelp', {}, 'Maks antall kunder i ett samplukk. Over 0 brukes dette i stedet for antallet per eske. Står begge på 0, er grensen 10.')}
                </p>
              </div>
              {modalButtons(() => setTypeForm(null))}
            </form>
          </div>
        </div>
      )}

      {/* Eske: opprett/rediger */}
      {packagingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            {modalHeader(editingPackagingNo == null
              ? t('manager.modules.batchPickingSetup.packaging.createTitle', {}, 'Opprett eske')
              : t('manager.modules.batchPickingSetup.packaging.editTitle', {}, 'Rediger eske'), () => setPackagingForm(null))}
            <form onSubmit={handleSubmitPackaging} className="p-6 space-y-4">
              {formErrorBox}
              <div className="grid grid-cols-3 gap-4">
                {packagingField('packagingTypeNo', t('manager.modules.batchPickingSetup.fields.noRequired', {}, 'Nr *'), 'integer', editingPackagingNo != null)}
                <div className="col-span-2">
                  <label className={labelClass}>{t('common.fields.description', {}, 'Beskrivelse')} *</label>
                  <input
                    type="text"
                    value={packagingForm.description}
                    onChange={(e) => { setPackagingForm({ ...packagingForm, description: e.target.value }); setFormError(null); }}
                    disabled={formLoading}
                    className={inputClass}
                    maxLength={100}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {packagingField('height', t('manager.modules.batchPickingSetup.fields.height', {}, 'Høyde'))}
                {packagingField('width', t('manager.modules.batchPickingSetup.fields.width', {}, 'Bredde'))}
                {packagingField('length', t('manager.modules.batchPickingSetup.fields.length', {}, 'Lengde'))}
              </div>
              <div className="grid grid-cols-3 gap-4">
                {packagingField('volume', t('manager.modules.batchPickingSetup.fields.volume', {}, 'Volum'))}
                {packagingField('fillFactorPercent', t('manager.modules.batchPickingSetup.fields.fillFactor', {}, 'Fyllingsgrad (%)'))}
                {packagingField('maxWeight', t('manager.modules.batchPickingSetup.fields.maxWeightRequired', {}, 'Maks vekt *'))}
              </div>
              <div className="grid grid-cols-3 gap-4">
                {packagingField('maxLength', t('manager.modules.batchPickingSetup.fields.maxLength', {}, 'Maks lengde'))}
                {packagingField('maxNumberOfLines', t('manager.modules.batchPickingSetup.fields.maxNumberOfLines', {}, 'Maks ordrelinjer'), 'integer')}
              </div>
              <p className="text-xs text-gray-500">
                {t('manager.modules.batchPickingSetup.packaging.help', {}, 'Volum legges inn manuelt og regnes ikke ut fra målene. Maks lengde brukes når eskens egne mål er 0. Maks ordrelinjer 0 betyr ubegrenset.')}
              </p>
              {modalButtons(() => setPackagingForm(null))}
            </form>
          </div>
        </div>
      )}

      {/* Esker i samplukk-typen */}
      {packagesFor && (
        <BatchTypePackagesModal
          batchType={packagesFor}
          batchTypes={batchTypes}
          packagingTypes={packagingTypes}
          onClose={() => {
            // Åpne linjer skal vise endringene fra modalen — også andre typer, siden en eske kan være flyttet
            Object.keys(packagesByType).forEach((no) => loadPackagesForType(Number(no)));
            setPackagesFor(null);
          }}
        />
      )}
    </div>
  );
};
